import { useEffect, useLayoutEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatDkk } from "@/lib/api";
import { Star, MapPin, Languages, Award, Check, Share2, CalendarDays, MessageCircle } from "lucide-react";
import { PageSkeleton } from "@/components/LoadingSkeleton";
import VerifiedQualifications from "@/components/profile/VerifiedQualifications";
import OnlineStatus from "@/components/OnlineStatus";
import Seo from "@/components/Seo";
import { absoluteAssetUrl, CANONICAL_ORIGIN } from "@/lib/seo";
import { expertBackgroundLabel } from "@/lib/expertBackground";
import ProfileAvatar from "@/components/profile/ProfileAvatar";
import { trackEvent } from "@/lib/analytics";

import { formatExpertName } from "@/lib/expertDisplay";
import { publicReputation } from "@/lib/publicReputation";

const firstName = (s) => (s || "").split(" ")[0];

function ProcessStep({ number, children }) {
  return <li className="flex items-start gap-2.5"><span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-soft font-semibold text-brand-green">{number}</span><span className="pt-0.5 leading-5">{children}</span></li>;
}

function readableBio(value) {
  const text = String(value || "").trim();
  if (!text) return [];
  const writtenParagraphs = text.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean);
  if (writtenParagraphs.length > 1 || text.length < 280) return writtenParagraphs;
  const sentences = text.match(/[^.!?]+[.!?]+(?:["'»”)]*)|[^.!?]+$/g) || [text];
  const paragraphs = [];
  for (let index = 0; index < sentences.length; index += 2) {
    paragraphs.push(sentences.slice(index, index + 2).join(" ").trim());
  }
  return paragraphs;
}

export default function ExpertProfile() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [cats, setCats] = useState([]);
  const [shareCopied, setShareCopied] = useState(false);
  const nav = useNavigate();

  useLayoutEffect(() => {
    setE(null);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [id]);

  useEffect(() => {
    api.get(`/experts/${id}`).then((r) => setE(r.data));
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, [id]);

  if (!e) return <PageSkeleton cards={3}/>;

  const catNameById = Object.fromEntries(cats.map((c) => [c.id, c.name]));
  const startRequest = () => {
    if (e.previously_helped_current_customer) {
      trackEvent("repeat_provider_clicked", { expert_id: id, metadata: { route: `/eksperter/${id}` } });
    }
    nav(`/beskriv-problem?ekspert=${id}${e.previously_helped_current_customer ? "&gentag=1" : ""}`);
  };

  const displayName = formatExpertName(e.name);
  const fName = firstName(displayName);
  const { hasRating, completedCount, reviews } = publicReputation(e);
  const reviewMetrics = ["expertise", "helpfulness", "communication"].map((key) => {
    const values = reviews.map((review) => review[key]).filter((value) => Number(value) > 0);
    return values.length ? values.reduce((sum, value) => sum + Number(value), 0) / values.length : null;
  });
  const helpText = e.help_summary || ((e.specialties || []).length
    ? `Få personlig hjælp til ${e.specialties.join(", ")} via chat og video.`
    : "Beskriv dit problem og få personlig vejledning fra eksperten.");
  const profilePath = `/eksperter/${e.id}`;
  const seoDescription = `${displayName} hjælper med ${e.headline || (e.specialties || []).join(", ")}. Se erfaring og få et tilbud gennem SpørgMig.`.slice(0, 170);
  const profileUrl = `${CANONICAL_ORIGIN}${profilePath}`;
  const shareProfile = async () => {
    const shareData = {
      title: `${displayName} på SpørgMig`,
      text: `Se ${displayName}s ekspertprofil på SpørgMig.`,
      url: profileUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(profileUrl);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 2500);
    } catch {
      window.prompt("Kopiér linket til ekspertprofilen:", profileUrl);
    }
  };
  const schemas = [{
    "@context": "https://schema.org", "@type": "Person", name: displayName,
    jobTitle: e.headline || undefined, image: e.photo_url ? absoluteAssetUrl(e.photo_url) : undefined,
    url: `${CANONICAL_ORIGIN}${profilePath}`, knowsAbout: (e.specialties || []).length ? e.specialties : undefined,
  }, {
    "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Forside", item: `${CANONICAL_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Eksperter", item: `${CANONICAL_ORIGIN}/eksperter` },
      { "@type": "ListItem", position: 3, name: displayName, item: `${CANONICAL_ORIGIN}${profilePath}` },
    ],
  }];

  return (
    <><Seo title={`${displayName} – ${e.headline || "Ekspert"} | SpørgMig`} description={seoDescription} path={profilePath} image={e.photo_url} schemas={schemas}/><div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="rounded-[24px] border border-brand-line bg-white p-5 shadow-[0_16px_45px_rgba(45,42,38,.05)] sm:p-7">
            <div className="flex items-start gap-4">
            <ProfileAvatar photoUrl={absoluteAssetUrl(e.photo_url)} name={displayName} size="lg" className="shrink-0"/>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-bold text-brand-ink">{displayName}</h1>
                <button
                  type="button"
                  onClick={shareProfile}
                  className="inline-flex items-center gap-1.5 rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs font-medium text-brand-body transition-colors hover:border-brand-green hover:text-brand-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2"
                  aria-label={`Del ${displayName}s profil`}
                  data-testid="share-expert-profile"
                >
                  {shareCopied ? <Check size={14} aria-hidden="true"/> : <Share2 size={14} aria-hidden="true"/>}
                  {shareCopied ? "Link kopieret" : "Del profil"}
                </button>
              </div>
              <p className="mt-1 text-base sm:text-lg text-brand-body">{e.headline}</p>
              {e.background_type && <div className="mt-2"><span className="inline-flex rounded-full bg-brand-soft px-2.5 py-1 text-xs font-medium text-brand-body" title="Oplyst af profilen">{expertBackgroundLabel(e.background_type)} · selvangivet</span></div>}
              <div className="mt-2 flex items-center gap-3 text-sm text-brand-body flex-wrap">
                {hasRating && (
                  <span className="flex items-center gap-1">
                    <Star size={13} className="fill-[#D97706] text-[#D97706]"/>
                    <span className="font-medium text-brand-ink">{e.rating_avg?.toFixed(2)}</span>
                    <span className="text-brand-muted">({e.rating_count})</span>
                  </span>
                )}
                {completedCount > 0 && <span>{completedCount} {completedCount === 1 ? "gennemført opgave" : "gennemførte opgaver"}</span>}
                {e.years_experience > 0 && <span>{e.years_experience} års erfaring</span>}
                {e.country && <span className="flex items-center gap-1"><MapPin size={13}/> {e.country}</span>}
              </div>
            </div>
            </div>
            <div className="mt-4 space-y-2 border-t border-brand-line pt-4 text-sm text-brand-body">
              {e.languages?.length > 0 && <p className="flex items-start gap-1.5"><Languages size={14} className="mt-1 shrink-0"/>{e.languages.join(" · ")}</p>}
              <Link to="/ekspertgodkendelse" className="inline-block text-xs link-underline">Profil godkendt af SpørgMig</Link>
            </div>
            <div className="mt-4 border-t border-brand-line pt-4 lg:hidden" data-testid="expert-mobile-action">
              <p className="text-lg font-semibold text-brand-ink">{e.price_min ? `Fra ${formatDkk(e.price_min)}` : "Pris oplyses i tilbud"}</p>
              <p className="mt-1 text-xs leading-5 text-brand-muted">Den endelige pris afhænger af opgaven. Du ser og accepterer tilbuddet, før du betaler.</p>
              <button onClick={startRequest} className="btn-primary mt-3 w-full text-sm" data-testid="get-help-from-expert-mobile">{e.previously_helped_current_customer ? "Få hjælp igen" : `Få hjælp af ${fName}`}</button>
              <p className="mt-2 text-center text-xs text-brand-muted">Gratis at sende en forespørgsel</p>
            </div>
          </div>

          <section className="mt-5 rounded-2xl border border-[#C9DED7] bg-brand-soft p-5 sm:p-6" data-testid="expert-help-summary">
            <div className="flex items-center gap-2 text-brand-green"><MessageCircle size={18}/><h2 className="font-display text-lg font-semibold">Sådan kan {fName} hjælpe dig</h2></div>
            <div className="mt-3 max-w-[68ch] space-y-3 text-[15px] leading-7 text-[#35534A]">
              {readableBio(helpText).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
            </div>
          </section>

          <VerifiedQualifications claims={e.professional_badges}/>

          <section className="mt-5 rounded-2xl border border-brand-line bg-white p-5 sm:p-6">
            <h2 className="font-display text-lg font-semibold text-brand-ink">Om {fName}</h2>
            <div className="mt-3 max-w-[68ch] space-y-3 text-[15px] leading-7 text-brand-body">
              {readableBio(e.bio).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
              {!e.bio && <p className="text-brand-muted">Eksperten har ikke tilføjet en beskrivelse endnu.</p>}
            </div>
          </section>

          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <div className="card-outline p-4">
              <div className="font-mono uppercase text-[10px] text-brand-muted tracking-widest">Specialer</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(e.specialties || []).length === 0 && <span className="text-xs text-brand-muted">Ingen angivet</span>}
                {(e.specialties || []).map((s) => <span key={s} className="chip">{s}</span>)}
              </div>
              {(e.category_ids?.length || e.category_id) && (
                <div className="mt-3 text-xs text-brand-muted">
                  Kategorier: <span className="text-brand-ink font-medium">{(e.category_ids || [e.category_id]).map((id) => catNameById[id]).filter(Boolean).join(" · ")}</span>
                </div>
              )}
              {(e.seo_links || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Relaterede hjælpeområder">
                  {e.seo_links.map((item) => (
                    <Link key={item.slug} to={`/hjaelp/${item.slug}`} className="rounded-full border border-brand-line px-2.5 py-1 text-[11px] text-brand-fresh hover:border-brand-fresh">
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="card-outline p-4">
              <div className="font-mono uppercase text-[10px] text-brand-muted tracking-widest">Sprog & erfaring</div>
              <div className="mt-1.5 space-y-1 text-sm text-brand-body">
                {e.languages?.length > 0 && <div className="flex items-center gap-1.5"><Languages size={13}/> {e.languages.join(", ")}</div>}
                {e.years_experience > 0 && <div className="flex items-center gap-1.5"><Award size={13}/> {e.years_experience} års erfaring</div>}
              </div>
            </div>
          </div>

          {reviews.length > 0 && <div className="mt-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-brand-ink">Anmeldelser</h2>
              {hasRating && <span className="text-xs text-brand-muted">Ø {e.rating_avg?.toFixed(2)} · {e.rating_count}</span>}
            </div>
            {reviewMetrics.some((value) => value != null) && <div className="mt-3 grid grid-cols-3 gap-2">
              {[["Faglighed", reviewMetrics[0]], ["Hjælpsomhed", reviewMetrics[1]], ["Kommunikation", reviewMetrics[2]]].map(([label, value]) => value != null && <div key={label} className="rounded-xl bg-brand-soft p-3 text-center"><div className="text-[11px] text-brand-muted">{label}</div><div className="mt-1 font-semibold text-brand-ink">{value.toFixed(1)} / 5</div></div>)}
            </div>}
            <div className="mt-3 space-y-2.5">
              {reviews.map((r) => (
                <div key={r.id} className="card-outline p-3.5" data-testid={`review-item-${r.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-0.5 text-[#D97706]">
                      {[1,2,3,4,5].map((n) => <Star key={n} size={12} className={n <= r.rating ? "fill-current" : "text-brand-line"}/>)}
                    </div>
                    {r.verified_task && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-brand-soft text-brand-green border border-brand-line-strong" data-testid={`review-verified-${r.id}`}>
                        <Check size={9}/> Gennemført opgave
                      </span>
                    )}
                  </div>
                  {(r.problem_title || r.category_id) && (
                    <div className="mt-1 text-sm text-brand-ink font-medium truncate">
                      {r.problem_title}
                      {r.category_id && catNameById[r.category_id] && (
                        <span className="text-brand-muted font-normal"> · {catNameById[r.category_id]}</span>
                      )}
                    </div>
                  )}
                  {r.comment && <p className="mt-1 text-sm text-brand-body leading-relaxed">{r.comment}</p>}
                  <div className="mt-1 text-[11px] text-brand-muted">
                    {firstName(r.customer_name)}
                    {r.duration_min && ` · ${r.duration_min} min konsultation`}
                    {r.created_at && ` · ${new Date(r.created_at).toLocaleDateString("da-DK")}`}
                  </div>
                </div>
              ))}
            </div>
          </div>}
        </div>

        <aside className="lg:col-span-1">
          <div className="card-outline p-5 sticky top-24">
            <div className="flex items-center justify-between">
              <OnlineStatus online={e.online}/>
            </div>
            <div className="mt-3 space-y-2 rounded-xl bg-brand-soft p-3 text-xs text-brand-body">
              <div className="flex items-start gap-2"><CalendarDays size={14} className="mt-0.5 shrink-0 text-brand-green"/><span>{e.availability_note || "Tidspunkt aftales med eksperten"}</span></div>
            </div>

            <div className="mt-4" data-testid="expert-profile-pricing">
              <div className="rounded-xl border border-brand-line bg-white p-4">
                <div className="text-[10px] uppercase tracking-widest text-brand-muted">Pris</div>
                <div className="mt-1 font-display text-xl font-bold text-brand-ink">{e.price_min ? `Fra ${formatDkk(e.price_min)}` : "Oplyses i tilbud"}</div>
                <div className="mt-1 text-xs text-brand-muted">Den endelige pris afhænger af opgaven. Du ser og accepterer tilbuddet, før du betaler.</div>
              </div>
            </div>
            <p className="mt-2 text-[11px] leading-5 text-brand-muted">Eksperten kan sende dig et tilbud med ydelse, forventet tid og pris, før du vælger.</p>

            <ol className="mt-4 space-y-3 text-xs text-brand-body">
              <ProcessStep number="1">Beskriv problemet og vedhæft eventuelt billeder.</ProcessStep>
              <ProcessStep number="2">{fName} kan gennemgå opgaven og sende et konkret forslag.</ProcessStep>
              <ProcessStep number="3">Du vælger selv, om du vil acceptere tilbuddet.</ProcessStep>
            </ol>

            {e.previously_helped_current_customer && <div className="mt-4 rounded-xl border border-brand-line-strong bg-[#EFF7F1] p-3 text-sm text-[#355B4E]" data-testid="previously-helped-banner">Du har tidligere fået hjælp af {e.name}.</div>}
            <button onClick={startRequest} className="btn-primary w-full mt-4 !py-2.5 text-sm" data-testid="get-help-from-expert">
              {e.previously_helped_current_customer ? "Få hjælp igen" : `Få hjælp af ${fName}`}
            </button>
            <div className="mt-2 text-[11px] text-brand-muted leading-snug">
              Gratis at sende en forespørgsel · Du vælger selv, om du vil acceptere et tilbud.
            </div>

            <p className="mt-3 text-xs leading-5 text-brand-muted">Spørgsmål om en opgave eller betaling? <Link to="/kontakt" className="link-underline">Kontakt SpørgMig</Link>.</p>

            <div className="mt-4 pt-3 border-t border-brand-soft text-[11px] text-brand-muted">
              Har du ikke en specifik ekspert i tankerne? <Link to="/beskriv-problem" className="link-underline">Beskriv dit problem</Link> så matcher vi dig.
            </div>
          </div>
        </aside>
      </div>
    </div></>
  );
}

